/** @format */

import { Component, OnInit } from "@angular/core"
import { forkJoin } from "rxjs"
import { DataPoint } from "src/app/models/datapoint.model"
import { DataPointService } from "src/app/services/data-point/data-point.service"
import { ActivatedRoute, Router } from "@angular/router"
import { moveItemInArray } from "@angular/cdk/drag-drop"
import { DialogService } from "src/app/services/modal/dialog.service"
import { ToastService } from "src/app/services/toast/toast.service"
import { LogsheetService } from "src/app/services/logsheet/logsheet.service"
import { ErrorHandlerService } from "src/app/services/errorHandler/error-handler.service"
import { SubmissionGuardService } from "src/app/services/submission-guard/submission-guard.service"
import { Roles } from "src/app/auth/roles";
import { IntegrationService } from "src/app/services/integration/integration.service"

interface SelectedField {
	dataPoint: DataPoint
	hiddenByDefault: boolean
	mandatoryWhenVisible: boolean
	requiredWhenVisible: boolean
	sourceDataPointId: number | null
	operator: string
	conditionValue: string
}

interface GuardFormItem {
	label: string
	guardType: 'Advisory' | 'Acknowledged'
	priority: number
	sourceDataPointId: number | null
	operator: string | null
	thresholdValue: string
	warningMessage: string
	expanded: boolean
}

@Component({
	standalone: false,
	selector: "app-add-logsheet",
	templateUrl: "./add-logsheet.component.html",
	styleUrls: ["./add-logsheet.component.css"],
})
export class AddLogsheetComponent implements OnInit {
	readonly Roles = Roles;
	selectedValues = {
		company: {
			companyId: 0,
			companyName: "",
		},
		plant: {
			plantId: 0,
			plantName: "",
		},
		unit: {
			unitId: 0,
			unitName: "",
		},
	}

	constructor(
		private dataPointService: DataPointService,
		private route: ActivatedRoute,
		private router: Router,
		public dialogService: DialogService,
		public toastService: ToastService,
		private logsheetService: LogsheetService,
		private errorService: ErrorHandlerService,
		private guardService: SubmissionGuardService,
		private integrationService: IntegrationService,
	) {}

	addLogsheetRequest = {
		logSheetTemplateId: 0,
		logSheetName: "",
		isDeleted: false,
		datapointId: [] as number[],
		unitId: 0,
		isEnabled: true,
		formType: "",
	}

	formTypeOptions = [
		{ id: 1, name: "Dynamic" },
		{ id: 2, name: "Static" },
	]

	errorMessage: any = {
		success: true,
		message: "",
	}

	activeDataPoints: DataPoint[] = []
	selectDataPoints: SelectedField[] = []
	activeStep = 1
	currentRole: any
	firstRender = false
	searchText: String = ""
	availableApiEndpoints: any[] = []
	selectedEndpoints: any[] = []
	selectedCloudIntegrations: any[] = []
	pickerOpen = false
	pickerScreen: 'category' | 'api-endpoints' = 'category'
	pickerSelectedId: number | null = null

	activeFieldIndex: number | null = null

	operatorOptions = [
		{ id: 1, name: "Equal (=)" },
		{ id: 2, name: "Not Equal (!=)" },
		{ id: 3, name: "Greater than (>)" },
		{ id: 4, name: "Less than (<)" },
		{ id: 5, name: "Is Empty" },
		{ id: 6, name: "Is Not Empty" },
	]

	guardOperatorOptions = [
		{ id: "<", name: "Less than (<)" },
		{ id: "<=", name: "Less than or equal (<=)" },
		{ id: ">", name: "Greater than (>)" },
		{ id: ">=", name: "Greater than or equal (>=)" },
		{ id: "==", name: "Equal (=)" },
		{ id: "!=", name: "Not Equal (!=)" },
		{ id: "contains", name: "Contains" },
	]

	guards: GuardFormItem[] = []

	get guardSourceFieldOptions() {
		return this.selectDataPoints.map(f => ({
			id: f.dataPoint.dataPointId,
			name: f.dataPoint.dataPointName,
		}))
	}

	get activeField(): SelectedField | null {
		if (this.activeFieldIndex === null) return null
		return this.selectDataPoints[this.activeFieldIndex] ?? null
	}

	get assignedApiEndpoints() {
		return this.selectedEndpoints
	}

	get pickerEndpoints() {
		const selectedIds = new Set(this.selectedEndpoints.map(endpoint => endpoint.apiEndpointConfigId))
		return this.availableApiEndpoints.filter(endpoint => !selectedIds.has(endpoint.apiEndpointConfigId))
	}

	get availableApiEndpointSummary() {
		if (this.availableApiEndpoints.length === 0) {
			return 'No endpoints configured'
		}

		return this.availableApiEndpoints.map(endpoint => endpoint.displayName).join(', ')
	}

	setActiveStep(step: number) {
		this.activeStep = step
	}

	openPicker() {
		this.pickerOpen = true
		this.pickerScreen = 'category'
		this.pickerSelectedId = null
	}

	closePicker() {
		this.pickerOpen = false
	}

	goToApiEndpoints() {
		this.pickerScreen = 'api-endpoints'
	}

	goToCategory() {
		this.pickerScreen = 'category'
	}

	selectPickerEndpoint(id: number) {
		this.pickerSelectedId = this.pickerSelectedId === id ? null : id
	}

	confirmPickerSelection() {
		if (this.pickerSelectedId === null) {
			this.pickerOpen = false
			return
		}

		const endpoint = this.availableApiEndpoints.find(
			item => item.apiEndpointConfigId === this.pickerSelectedId
		)
		const alreadySelected = this.selectedEndpoints.some(
			item => item.apiEndpointConfigId === this.pickerSelectedId
		)

		if (endpoint && !alreadySelected) {
			this.selectedEndpoints.push(endpoint)
		}

		this.pickerSelectedId = null
		this.pickerOpen = false
	}

	removeApiEndpointAssignment(id: number) {
		this.selectedEndpoints = this.selectedEndpoints.filter(
			endpoint => endpoint.apiEndpointConfigId !== id
		)
	}

	removeCloudAssignment(id: number) {
		this.selectedCloudIntegrations = this.selectedCloudIntegrations.filter(
			integration => integration.cloudIntegrationId !== id
		)
	}

	navigateToIntegrations() {
		this.router.navigate(['/settings/integrations'])
	}

	isSelectedDataPoint(dataPoint: DataPoint) {
		return this.selectDataPoints.findIndex(x => x.dataPoint === dataPoint) >= 0
	}

	showSaveModal() {
		this.dialogService.showModal = true
	}

	isValidLogsheetName() {
		return (
			this.addLogsheetRequest.logSheetName.length >= 3 &&
			this.addLogsheetRequest.logSheetName.length <= 50
		)
	}

	isValidFormType() {
		return (
			this.addLogsheetRequest.formType === "Dynamic" ||
			this.addLogsheetRequest.formType === "Static"
		)
	}

	onFormTypeSelect(event: any) {
		this.addLogsheetRequest.formType = event.name
	}

	addDataPoint(data: DataPoint) {
		this.selectDataPoints.push({
			dataPoint: data,
			hiddenByDefault: false,
			mandatoryWhenVisible: true,
			requiredWhenVisible: false,
			sourceDataPointId: null,
			operator: "Equal (=)",
			conditionValue: "",
		})
	}

	removeDataPoint(index: number) {
		this.selectDataPoints.splice(index, 1)
		if (this.activeFieldIndex === index) {
			this.activeFieldIndex = null
		} else if (this.activeFieldIndex !== null && this.activeFieldIndex > index) {
			this.activeFieldIndex--
		}
	}

	toggleFieldPanel(index: number) {
		this.activeFieldIndex = this.activeFieldIndex === index ? null : index
	}

	getSourceFieldOptions(excludeIndex: number) {
		return this.selectDataPoints
			.filter((_, i) => i !== excludeIndex)
			.map(f => ({ id: f.dataPoint.dataPointId, name: f.dataPoint.dataPointName }))
	}

	getSourceFieldLabel(field: SelectedField): string {
		if (!field.sourceDataPointId) return "Select field"
		const found = this.selectDataPoints.find(
			f => f.dataPoint.dataPointId === field.sourceDataPointId
		)
		return found ? found.dataPoint.dataPointName : "Select field"
	}

	onSourceFieldSelect(event: any, field: SelectedField) {
		field.sourceDataPointId = event.id
	}

	onOperatorSelect(event: any, field: SelectedField) {
		field.operator = event.name
	}

	getBadgeClass(field: SelectedField): string {
		return field.hiddenByDefault ? "badge-conditional" : "badge-always"
	}

	onFieldBadgeClick(index: number, event: MouseEvent) {
		event.stopPropagation()
		const field = this.selectDataPoints[index]
		if (!field) return

		field.hiddenByDefault = !field.hiddenByDefault
		if (field.hiddenByDefault) {
			field.mandatoryWhenVisible = false
			field.requiredWhenVisible = false
		} else {
			field.sourceDataPointId = null
			field.operator = "Equal (=)"
			field.conditionValue = ""
			field.requiredWhenVisible = false
		}

		this.activeFieldIndex = index
	}

	onToggleHiddenByDefault(enabled: boolean) {
		if (this.activeField) {
			this.activeField.hiddenByDefault = enabled
			if (enabled) {
				this.activeField.mandatoryWhenVisible = false
				this.activeField.requiredWhenVisible = false
			} else {
				this.activeField.sourceDataPointId = null
				this.activeField.operator = "Equal (=)"
				this.activeField.conditionValue = ""
				this.activeField.requiredWhenVisible = false
			}
		}
	}

	onToggleMandatoryWhenVisible(enabled: boolean) {
		if (this.activeField) {
			if (enabled) {
				this.activeField.hiddenByDefault = false
				this.activeField.sourceDataPointId = null
				this.activeField.operator = "Equal (=)"
				this.activeField.conditionValue = ""
				this.activeField.requiredWhenVisible = false
			}
			this.activeField.mandatoryWhenVisible = enabled
		}
	}

	onToggleRequiredWhenVisible(enabled: boolean) {
		if (this.activeField && this.activeField.hiddenByDefault) {
			this.activeField.mandatoryWhenVisible = false
			this.activeField.requiredWhenVisible = enabled
		} else if (this.activeField) {
			this.activeField.requiredWhenVisible = false
		}
	}

	// ─── Guard methods ───────────────────────────────────────────────────────────

	addGuard() {
		this.guards.push({
			label: "New Submission Guard",
			guardType: "Acknowledged",
			priority: this.guards.length + 1,
			sourceDataPointId: null,
			operator: null,
			thresholdValue: "",
			warningMessage: "",
			expanded: false,
		})
	}

	removeGuard(index: number) {
		this.guards.splice(index, 1)
		this.guards.forEach((g, i) => (g.priority = i + 1))
	}

	toggleGuard(index: number) {
		this.guards[index].expanded = !this.guards[index].expanded
	}

	selectGuardType(index: number, type: "Advisory" | "Acknowledged") {
		this.guards[index].guardType = type
	}

	onGuardSourceFieldSelect(event: any, guard: GuardFormItem) {
		guard.sourceDataPointId = event.id
	}

	onGuardOperatorSelect(event: any, guard: GuardFormItem) {
		guard.operator = event.id
	}

	getGuardSourceFieldLabel(guard: GuardFormItem): string {
		if (!guard.sourceDataPointId) return "Select field"
		const found = this.selectDataPoints.find(
			f => f.dataPoint.dataPointId === guard.sourceDataPointId
		)
		return found ? found.dataPoint.dataPointName : "Select field"
	}

	getGuardOperatorLabel(guard: GuardFormItem): string {
		if (!guard.operator) return "Select function"
		const found = this.guardOperatorOptions.find(o => o.id === guard.operator)
		return found ? found.name : guard.operator
	}

	getGuardWarningPlaceholder(guardType: string): string {
		return guardType === 'Advisory'
			? 'e.g. "The temperature you have submitted ({value}) is above the normal threshold."'
			: 'e.g. "Please confirm you have reviewed this entry before proceeding."'
	}

	// ─── Navigation ─────────────────────────────────────────────────────────────

	transferData() {
		this.firstRender = true
		this.router.navigateByUrl("settings/forms", {
			state: {
				selectedValues: { ...this.selectedValues },
				firstRender: this.firstRender,
			},
		})
	}

	ngOnInit(): void {
		this.currentRole = localStorage.getItem("role")
		this.selectedValues = history.state

		this.dataPointService
			.retrieveDataPointByUnitId(
				this.selectedValues.unit.unitId.toString(),
			)
			.subscribe({
				next: (dataPoints) => {
					this.activeDataPoints = dataPoints
				},
				error: (err) => {
					this.errorMessage = this.errorService.handleRetrievalErrors(err)
				},
			})

		this.integrationService.retrieveApiIntegrations().subscribe({
			next: (endpoints) => {
				this.availableApiEndpoints = endpoints
			},
			error: (err) => {
				this.errorService.handleError(err, "Error retrieving API integrations")
			},
		})
	}

	areGuardsValid(): boolean {
		return this.guards.every(g => {
			const base =
				g.label.trim().length > 0 &&
				g.priority >= 1 &&
				g.warningMessage.trim().length > 0
			if (g.guardType === 'Advisory') {
				return base &&
					g.sourceDataPointId !== null &&
					!!g.operator &&
					g.thresholdValue.trim().length > 0
			}
			return base
		})
	}

	enableNextBtn() {
		if (this.activeStep === 1 && (!this.isValidLogsheetName() || !this.isValidFormType())) {
			return false
		}
		if (this.activeStep === 2 && this.selectDataPoints.length === 0) {
			return false
		}
		if (this.activeStep === 3 && !this.areGuardsValid()) {
			return false
		}
		return true
	}

	drop(event: any) {
		moveItemInArray(
			this.selectDataPoints,
			event?.previousIndex,
			event?.currentIndex,
		)
		if (this.activeFieldIndex !== null) {
			const prev = event.previousIndex
			const curr = event.currentIndex
			if (prev === this.activeFieldIndex) {
				this.activeFieldIndex = curr
			} else if (prev < this.activeFieldIndex && curr >= this.activeFieldIndex) {
				this.activeFieldIndex--
			} else if (prev > this.activeFieldIndex && curr <= this.activeFieldIndex) {
				this.activeFieldIndex++
			}
		}
	}

	private onSaveSuccess() {
		this.transferData()
		this.toastService.showToast = true
		this.toastService.toastText = "Form successfully added!"
		setTimeout(() => {
			this.toastService.showToast = false
		}, 5000)
	}

	private saveRelatedConfiguration(templateId: number) {
		const guardRequests = this.guards.map(g =>
			this.guardService.createGuard({
				templateId,
				label: g.label,
				sourceDataPointId: g.sourceDataPointId,
				operator: g.operator,
				thresholdValue: g.thresholdValue || null,
				guardType: g.guardType,
				warningMessage: g.warningMessage,
				priority: g.priority,
				isActive: true,
			})
		)
		const endpointRequests = this.selectedEndpoints.map(endpoint =>
			this.integrationService.assignEndpointToForm(
				templateId,
				endpoint.apiEndpointConfigId
			)
		)
		const requests = [...guardRequests, ...endpointRequests]

		if (requests.length === 0) {
			this.onSaveSuccess()
			return
		}

		forkJoin(requests).subscribe({
			next: () => this.onSaveSuccess(),
			error: (err) => {
				if (endpointRequests.length > 0) {
					this.toastService.showToast = true
					this.toastService.isError = true
					this.toastService.toastText =
						err?.error?.message || "Error assigning API integrations"
					setTimeout(() => {
						this.toastService.showToast = false
					}, 5000)
					return
				}

				this.errorService.handleError(err, "Error saving submission guards")
			},
		})
	}

	addLogsheet() {
		this.addLogsheetRequest.unitId = this.selectedValues.unit.unitId

		// Build payload with conditional metadata nested in dataPoints[].fieldCondition.
		const { datapointId: _, ...baseRequest } = this.addLogsheetRequest
		const payload = {
			...baseRequest,
			dataPoints: this.selectDataPoints.map((f, index) => ({
				dataPointId: f.dataPoint.dataPointId,
				order: index,
				hiddenByDefault: f.hiddenByDefault,
				mandatoryWhenVisible: f.mandatoryWhenVisible,
				// Backward-compat for older API contracts that still read top-level requiredWhenVisible.
				requiredWhenVisible: f.mandatoryWhenVisible,
				sourceDataPointId: f.hiddenByDefault ? f.sourceDataPointId : null,
				operator: f.hiddenByDefault ? f.operator : null,
				conditionValue: f.hiddenByDefault ? f.conditionValue : null,
				fieldCondition: f.hiddenByDefault
					? {
							hiddenFieldId: f.dataPoint.dataPointId,
							sourceFieldId: f.sourceDataPointId || 0,
							condition: f.operator || "Equal (=)",
							value: f.conditionValue || "",
							requiredWhenVisible: f.requiredWhenVisible,
							isDeleted: false,
					  }
					: null,
			})),
		}

		this.logsheetService.addLogSheet(payload as any).subscribe({
			next: (response: any) => {
				const templateId = response?.logSheetTemplateId
				if (templateId) {
					localStorage.setItem(
						`formFields_${templateId}`,
						JSON.stringify(
							this.selectDataPoints.map((f, index) => ({
								dataPointId: f.dataPoint.dataPointId,
								order: index,
								hiddenByDefault: f.hiddenByDefault,
								mandatoryWhenVisible: f.mandatoryWhenVisible,
								requiredWhenVisible: f.requiredWhenVisible,
								sourceDataPointId: f.sourceDataPointId,
								operator: f.hiddenByDefault ? f.operator : null,
								conditionValue: f.hiddenByDefault ? f.conditionValue : null,
							}))
						)
					)

					this.saveRelatedConfiguration(templateId)
				} else {
					this.onSaveSuccess()
				}
			},
			error: (response) => {
				this.errorService.handleError(response, "Error adding a Form")
			},
		})
	}
}
